import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { OvertimeManagementService } from '../../services/overtime-management.service';
import { FilterService } from '../../../../_shared/services';
import { PaginatedDriversRequest } from '../../models/get-paginated-drivers-request.model';
import { endOfDay, startOfDay } from 'date-fns';
import { OvertimeDriver } from '../../models/overtime-drivers.model';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../_shared/models';
import { debounceTime, distinctUntilChanged, Subscription, Subject } from 'rxjs';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { AvatarComponent } from '../../../../_shared/components/avatar/avatar.component';
import { OvertimeDriverStateService } from '../../services/overtime-driver-state.service';
import { OvertimeManagementListService } from '../../services/overtime-management-list.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { CurrencyHelper } from '../../../../_shared/helpers/currency/currency-map.helper';
import { AuthService } from '../../../../_shared/services/auth.service';

import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-overtime-driver-list',
  standalone: true,
  imports: [CommonModule, InfiniteScrollModule, AvatarComponent, TranslocoPipe],
  templateUrl: './overtime-driver-list.component.html',
  styleUrl: './overtime-driver-list.component.scss'
})
export class OvertimeDriverListComponent implements OnInit, OnChanges {
  @Input() showOnlyRequiresDecision: boolean = false;
  @Output() loaded = new EventEmitter<boolean>();

  public filteredDrivers: any[] = [];
  public selectedDriver: OvertimeDriver;
  public driverListLoading: boolean = false;
  public isSearching: boolean = false;
  public overtimeDrivers: OvertimeDriver[] = [];
  public paginatedDriversRequest: PaginatedDriversRequest = {
    accountIds: [],
    timeFrom: startOfDay(new Date()),
    timeTo: endOfDay(new Date()),
    page: 1,
    pageSize: 15,
    getPendingOnly: this.showOnlyRequiresDecision
  };

  public dateFilter: DateIntervalFilterData;
  public accountFilter: AccountFilterData;
  public totalPages: number = 1;
  public selectedDriverIndex: number;
  public currencySymbol: string = '';
  public currencyCode: string = '';

  private filterSubscription: Subscription;
  private driverSearchSubscription: Subscription;
  private newTotalCostSubscription: Subscription;
  private cancellation: Subject<void> = new Subject<void>();

  private overtimeManagementService = inject(OvertimeManagementService);
  private overtimeDriverStateService = inject(OvertimeDriverStateService);
  private filterService = inject(FilterService);
  private overtimeManagementListService = inject(OvertimeManagementListService);
  private errorHandlerService = inject(ErrorHandlerService);
  private auth = inject(AuthService);

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  ngOnInit(): void {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe((filter) => {
      if (!filter || Object.values(filter).every(x => x == null)) {
        return;
      }
      this.updateDateFilter(filter);
      this.paginatedDriversRequest.accountIds = filter.accountIds ?? [];
      this.onFilterChangeAsync();
    });

    this.driverSearchSubscription = this.filterService.searchTerm$.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe(async searchTerm => {
      this.overtimeDrivers = [];
      this.paginatedDriversRequest.page = 1;
      this.paginatedDriversRequest.searchTerm = searchTerm;

      await this.getOvertimeDriverList();
    });

    this.newTotalCostSubscription = this.overtimeDriverStateService.totalCostUpdated.subscribe((newCost) => {
      if (newCost) {
        this.overtimeDrivers[this.selectedDriverIndex].totalCost = newCost;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    let change = changes['showOnlyRequiresDecision'];
    if (!change.firstChange) {
      this.overtimeDrivers = [];
      this.paginatedDriversRequest.page = 1;
      this.paginatedDriversRequest.getPendingOnly = this.showOnlyRequiresDecision;
      this.getOvertimeDriverList();
    }
  }

  ngOnDestroy(): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
    if (this.driverSearchSubscription) {
      this.driverSearchSubscription.unsubscribe();
    }
    if (this.newTotalCostSubscription) {
      this.newTotalCostSubscription.unsubscribe();
    }

    this.cancellation.next();
    this.cancellation.complete();
  }

  async onFilterChangeAsync(): Promise<void> {
    this.overtimeDrivers = [];
    this.paginatedDriversRequest.page = 1;
    this.paginatedDriversRequest.timeFrom = this.dateFilter.startDate!;
    this.paginatedDriversRequest.timeTo = this.dateFilter.endDate!;

    await this.getOvertimeDriverList();
  }

  async getOvertimeDriverList(): Promise<void> {
    this.loaded.emit(false);
    this.driverListLoading = true;
    try {
      this.cancellation.next();
      const previouslySelectedDriver = this.overtimeDrivers.find(driver => this.selectedDriver.id === driver.id);
      const response = await this.overtimeManagementService.getPaginatedDrivers(this.paginatedDriversRequest, this.cancellation);
      const drivers = response.data?.results!;

      this.overtimeDrivers = [...this.overtimeDrivers, ...drivers];
      this.totalPages = response.data?.totalPages!;

      const matchedIndex = this.overtimeDrivers.findIndex(driver => driver.id === previouslySelectedDriver?.id);

      if (matchedIndex !== -1) {
        this.selectedDriver = this.overtimeDrivers[matchedIndex];
        this.selectedDriverIndex = matchedIndex;
      }
      else if (this.overtimeDrivers.length > 0 && this.paginatedDriversRequest.page === 1) {
        this.selectedDriver = this.overtimeDrivers[0];
        this.selectedDriverIndex = 0;
        this.overtimeDriverStateService.setSelectedDriver(this.selectedDriver);
      }

      this.overtimeManagementListService.updateDriverOvertimes(this.overtimeDrivers);

      this.loaded.emit(true);
      this.driverListLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.loaded.emit(true);
          this.driverListLoading = false;
        }
      });
    }
  }

  formatCost(cost: number): string {
    return `${this.currencySymbol}${cost.toLocaleString('default', { maximumFractionDigits: 2 })}`;
  }

  updateDateFilter(widgetFilter: WidgetFilterData): void {
    if (!widgetFilter) {
      return;
    }

    this.dateFilter.endDate = widgetFilter.endDate;
    this.dateFilter.startDate = widgetFilter.startDate;
    this.dateFilter.granularity = widgetFilter.granularity;
    this.dateFilter.previousCycleEndDate = widgetFilter.previousCycleEndDate;
    this.dateFilter.previousCycleStartDate = widgetFilter.previousCycleStartDate;
    this.dateFilter.interval = widgetFilter.interval;
  }

  updateAccountFilter(widgetFilter: WidgetFilterData): void {
    // TODO: Need to test if this works properly
    if (!this.accountFilter.accountIds) {
      return;
    }

    this.accountFilter.accountIds = widgetFilter.accountIds;
  }

  public toggleDriverSelection(index: number): void {
    if (this.selectedDriver !== this.overtimeDrivers[index]) {
      this.overtimeDriverStateService.setSelectedDriver(this.overtimeDrivers[index]);
      this.selectedDriver = this.overtimeDrivers[index];
      this.selectedDriverIndex = index;
    }
  }

  onScrollDown(): void {
    if (this.paginatedDriversRequest.page < this.totalPages) {
      this.paginatedDriversRequest.page++;
      this.getOvertimeDriverList();
    }
  }
}